import {
  useAdminDashboardCounts,
  useDashboardAlerts,
  type AdminDashboardCounts,
} from '@/hooks/useAdminDashboardCounts'

function formatTrend(
  current: number,
  previous: number,
): { direction: 'up' | 'down' | 'same'; label: string; pct: number } {
  if (previous === 0) {
    return {
      direction: current > 0 ? 'up' : 'same',
      label: current > 0 ? `Up from 0 (${current} this period)` : 'No change',
      pct: current > 0 ? 100 : 0,
    }
  }
  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct > 0)
    return { direction: 'up', label: `Up ${pct}% from previous 7 days`, pct }
  if (pct < 0)
    return { direction: 'down', label: `Down ${Math.abs(pct)}% from previous 7 days`, pct }
  return { direction: 'same', label: 'No change from previous 7 days', pct: 0 }
}

function MetricCard({
  label,
  value,
  description,
}: {
  label: string
  value: string | number
  description?: string
}) {
  return (
    <div
      className="rounded border-2 p-4 min-h-[44px]"
      style={{ borderColor: 'var(--bg-tertiary)' }}
    >
      <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </dt>
      <dd className="mt-1 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
        {value}
      </dd>
      {description && (
        <dd className="mt-0.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          {description}
        </dd>
      )}
    </div>
  )
}

function TrendsSection({ counts }: { counts: AdminDashboardCounts }) {
  const userTrend = formatTrend(counts.new_users_7d, counts.new_users_prev_7d)
  const threadTrend = formatTrend(counts.new_threads_7d, counts.new_threads_prev_7d)
  const commentTrend = formatTrend(counts.new_comments_7d, counts.new_comments_prev_7d)

  return (
    <section aria-labelledby="trends-heading" className="mt-8">
      <h2 id="trends-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        Trends (week on week)
      </h2>
      <ul className="list-none p-0 m-0 space-y-2">
        <li>
          <span style={{ color: 'var(--text-primary)' }}>Users: </span>
          <span role="text" aria-label={userTrend.label}>
            {userTrend.direction === 'up' && (
              <>
                <span aria-hidden="true">↑ {userTrend.pct}%</span>
              </>
            )}
            {userTrend.direction === 'down' && (
              <>
                <span aria-hidden="true">↓ {Math.abs(userTrend.pct)}%</span>
              </>
            )}
            {userTrend.direction === 'same' && (
              <span aria-hidden="true">No change</span>
            )}
          </span>
        </li>
        <li>
          <span style={{ color: 'var(--text-primary)' }}>Threads: </span>
          <span role="text" aria-label={threadTrend.label}>
            {threadTrend.direction === 'up' && <span aria-hidden="true">↑ {threadTrend.pct}%</span>}
            {threadTrend.direction === 'down' && (
              <span aria-hidden="true">↓ {Math.abs(threadTrend.pct)}%</span>
            )}
            {threadTrend.direction === 'same' && <span aria-hidden="true">No change</span>}
          </span>
        </li>
        <li>
          <span style={{ color: 'var(--text-primary)' }}>Comments: </span>
          <span role="text" aria-label={commentTrend.label}>
            {commentTrend.direction === 'up' && <span aria-hidden="true">↑ {commentTrend.pct}%</span>}
            {commentTrend.direction === 'down' && (
              <span aria-hidden="true">↓ {Math.abs(commentTrend.pct)}%</span>
            )}
            {commentTrend.direction === 'same' && <span aria-hidden="true">No change</span>}
          </span>
        </li>
        <li>
          <span style={{ color: 'var(--text-primary)' }}>Listings forward coverage: </span>
          <span aria-label={`${counts.tv_listings_days} days`}>
            {counts.tv_listings_days} days
          </span>
        </li>
      </ul>
    </section>
  )
}

function AlertsSection({ alerts }: { alerts: ReturnType<typeof useDashboardAlerts> }) {
  if (alerts.length === 0) return null

  return (
    <section
      aria-labelledby="alerts-heading"
      className="mt-8"
      role="alert"
      aria-live="polite"
    >
      <h2 id="alerts-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        Alerts
      </h2>
      <ul className="list-none p-0 m-0 space-y-2">
        {alerts.map((a) => (
          <li
            key={a.id}
            className="flex items-start gap-2 rounded border-2 p-3"
            style={{
              borderColor: 'var(--color-warning)',
              color: 'var(--text-primary)',
            }}
          >
            <span aria-hidden="true" className="shrink-0">
              ⚠
            </span>
            <span>
              <strong>{a.label}</strong>: {a.description}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default function AdminDashboardPage() {
  const { data: counts, isLoading, error } = useAdminDashboardCounts()
  const alerts = useDashboardAlerts(counts ?? null)

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-4">
        <p style={{ color: 'var(--text-secondary)' }}>Loading dashboard…</p>
      </div>
    )
  }

  if (error) {
    return (
      <section aria-labelledby="dashboard-error-heading">
        <h1 id="dashboard-error-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Admin overview
        </h1>
        <p role="alert" style={{ color: 'var(--color-error)' }}>
          Failed to load dashboard: {error instanceof Error ? error.message : String(error)}.
        </p>
      </section>
    )
  }

  if (!counts) {
    return (
      <section aria-labelledby="dashboard-empty-heading">
        <h1 id="dashboard-empty-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Admin overview
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          You do not have permission to view dashboard counts, or no data is available.
        </p>
      </section>
    )
  }

  return (
    <section aria-labelledby="admin-overview-heading">
      <h1 id="admin-overview-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        Admin overview
      </h1>

      <section aria-labelledby="key-metrics-heading" className="mt-6">
        <h2 id="key-metrics-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Key metrics
        </h2>
        <dl className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <MetricCard label="Total users" value={counts.total_users.toLocaleString()} />
          <MetricCard
            label="Active users (7d)"
            value={counts.active_7d.toLocaleString()}
            description="Registered in last 7 days"
          />
          <MetricCard
            label="Active users (30d)"
            value={counts.active_30d.toLocaleString()}
            description="Registered in last 30 days"
          />
          <MetricCard label="Total threads" value={counts.total_threads.toLocaleString()} />
          <MetricCard label="Total comments" value={counts.total_comments.toLocaleString()} />
          <MetricCard
            label="Accessibility listings"
            value={counts.accessibility_listings.toLocaleString()}
            description="Programme slots with AD/Subtitles/BSL"
          />
          <MetricCard
            label="Broadcaster interactions"
            value={counts.broadcaster_interactions.toLocaleString()}
            description="Placeholder"
          />
          <MetricCard
            label="Pending reports"
            value={counts.pending_reports_count.toLocaleString()}
          />
        </dl>
      </section>

      <TrendsSection counts={counts} />
      <AlertsSection alerts={alerts} />
    </section>
  )
}
